import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormGroup, Label, Alert } from "reactstrap";
import { Upload, Image as ImageIcon } from "lucide-react";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";

interface ImageUploadProps {
  label: string;
  currentImage?: string;
  onUpload: (url: string) => void;
  folder: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  currentImage,
  onUpload,
  folder,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      // Reset error state
      setError(null);
      setUploading(true);

      try {
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error("File size exceeds 5MB limit");
        }

        const storage = firebase.storage();
        const storageRef = storage.ref();
        const fileRef = storageRef.child(
          `${folder}/${Date.now()}_${file.name}`
        );

        // Upload the file with metadata
        const metadata = {
          contentType: file.type,
          customMetadata: {
            originalName: file.name,
          },
        };

        await fileRef.put(file, metadata);
        const url = await fileRef.getDownloadURL();

        onUpload(url);
      } catch (error) {
        console.error("Error uploading image:", error);
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Failed to upload image. Please try again.");
        }
      } finally {
        setUploading(false);
      }
    },
    [folder, onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <FormGroup>
      <Label className="fw-bold">{label}</Label>
      <div
        {...getRootProps()}
        className={`border rounded p-4 text-center ${
          isDragActive ? "bg-light" : ""
        } ${uploading ? "opacity-50" : ""}`}
        style={{ cursor: uploading ? "not-allowed" : "pointer" }}
      >
        <input {...getInputProps()} />
        {currentImage ? (
          <div className="mb-3">
            <img
              src={currentImage}
              alt="Current"
              style={{ maxWidth: "200px", maxHeight: "200px" }}
              className="img-thumbnail"
            />
          </div>
        ) : (
          <ImageIcon size={48} className="text-muted mb-3" />
        )}
        <div className="d-flex align-items-center justify-content-center">
          <Upload size={20} className="me-2" />
          {uploading ? (
            <span>Uploading...</span>
          ) : isDragActive ? (
            <span>Drop the image here</span>
          ) : (
            <span>Drag & drop an image here, or click to select</span>
          )}
        </div>
      </div>
      {error && (
        <Alert color="danger" className="mt-2">
          {error}
        </Alert>
      )}
      <small className="text-muted d-block mt-2">
        Supported formats: JPEG, PNG. Max size: 5MB
      </small>
    </FormGroup>
  );
};

export default ImageUpload;
